import { RAFT } from "@eldrun/content";
import { BoxGeometry, ConeGeometry, CylinderGeometry, Group, Mesh, PlaneGeometry, type Material, type Object3D } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import { unindexed } from "../render/geometry/unindexed.ts";

export interface RaftLike {
  x: number;
  z: number;
  yaw: number;
  stage: string;
  parts: { get(id: string): { done: boolean; stage: number } | undefined };
}

/** O que aparece da Jangada pelo reparo: casco inteiro, mastro, vela, leme e quantos Muiraquitãs no Altar. */
export function raftLook(raft: RaftLike, relic: number): { hull: boolean; mast: boolean; sail: boolean; rudder: boolean; stones: number } {
  const done = (id: string) => raft.parts.get(id)?.done === true;
  return { hull: done("casco"), mast: done("mastro"), sail: done("vela"), rudder: done("leme"), stones: Math.min(5, relic) };
}

/**
 * A Jangada (doc 01): convés de 12 × 6 m no rumo da travessia; com o reparo, o casco inteiro, o
 * mastro, a vela, o leme e os Muiraquitãs acesos no Altar. Anda na travessia e ancora na Boca.
 */
export class RaftLayer {
  private readonly root = new Group();
  private readonly parts: { hull: Object3D; broken: Object3D; mast: Object3D; sail: Object3D; rudder: Object3D; stones: Mesh[] };
  private readonly materials: Material[] = [];

  constructor(scene: Object3D, atmosphere: AtmosphereUniforms) {
    const paint = (hex: string, glow = false) => {
      // Construção: na máscara do contorno (F51).
      const m = toonMaterial(atmosphere, hex, 0.8, glow, { outline: true });
      this.materials.push(m);
      return m;
    };
    const wood = paint("#8A6440");
    const dark = paint("#5A3E26");
    const cloth = paint("#E8DCC0");
    const stone = paint("#7C7A72");
    const L = RAFT.deck.length;
    const W = RAFT.deck.width;
    const mesh = (g: BoxGeometry | CylinderGeometry | ConeGeometry | PlaneGeometry, m: Material, x: number, y: number, z: number) => {
      const o = new Mesh(unindexed(g), m);
      o.position.set(x, y, z);
      return o;
    };
    const hull = new Group();
    for (let i = 0; i < 7; i++) hull.add(mesh(new BoxGeometry(W / 7 - 0.05, 0.2, L), wood, -W / 2 + (i + 0.5) * (W / 7), 0.4, 0));
    hull.add(mesh(new BoxGeometry(W + 0.3, 0.5, 0.3), dark, 0, 0.35, L / 2), mesh(new BoxGeometry(W + 0.3, 0.5, 0.3), dark, 0, 0.35, -L / 2));
    const broken = new Group();
    for (let i = 0; i < 4; i++) broken.add(mesh(new BoxGeometry(W / 7 - 0.05, 0.2, L * 0.6), wood, -W / 2 + (i * 2 + 0.5) * (W / 7), 0.3, -L * 0.15));
    const mast = mesh(new CylinderGeometry(0.15, 0.2, 7, 6), dark, 0, 4, 0);
    const sail = mesh(new PlaneGeometry(4, 4.5), cloth, 0, 4.5, 0.25);
    const rudder = mesh(new BoxGeometry(0.2, 1.4, 1.2), dark, 0, 0.6, -L / 2 - 0.5);
    const altar = new Group();
    altar.add(mesh(new BoxGeometry(1.2, 0.6, 0.8), stone, 0, 0.8, L / 2 - 1.2));
    const colors = ["#3FAF5A", "#3A8FD9", "#D9442E", "#F2C230", "#F07AB8"];
    const stones = colors.map((c, i) => {
      const s = mesh(new ConeGeometry(0.1, 0.25, 4), paint(c, true), -0.4 + i * 0.2, 1.25, L / 2 - 1.2);
      altar.add(s);
      return s;
    });
    this.root.add(hull, broken, mast, sail, rudder, altar);
    this.parts = { hull, broken, mast, sail, rudder, stones };
    scene.add(this.root);
  }

  update(raft: RaftLike, relic: number, y: number): void {
    this.root.position.set(raft.x, y, raft.z);
    this.root.rotation.y = raft.yaw;
    const look = raftLook(raft, relic);
    this.parts.hull.visible = look.hull || raft.stage !== "reparo";
    this.parts.broken.visible = !this.parts.hull.visible;
    this.parts.mast.visible = look.mast;
    this.parts.sail.visible = look.sail;
    this.parts.rudder.visible = look.rudder;
    this.parts.stones.forEach((s, i) => (s.visible = i < look.stones));
  }

  dispose(): void {
    this.root.removeFromParent();
    this.root.traverse((o) => (o as Mesh).geometry?.dispose());
    for (const m of this.materials) m.dispose();
  }
}
